import React from "react";
import { connect } from "react-redux";
import moment from "moment";
import { Button } from "../../Button";
import { Input, Select } from "../../Input";
import { Modal, DeleteModal } from "../../Modal";
import toastMessage from "../../../utils/toastMessage";
import centerQuery from "../../../utils/queries/bloodCenterQuery";
import fetchHemovigilancePlan, {
  createHemovigilancePlan,
  updateHemovigilancePlan,
  deleteHemovigilancePlan,
  recordHemovigilancePerformance,
} from "../../../utils/queries/hemovigilancePlanQuery";
import { defineRoleCenterSite, hasAllCenters } from "../../../utils/handleAccessRoles";
import { getStorage } from "../../../utils/storage";

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => {
  const y = currentYear - 2 + i;
  return { label: String(y), value: y };
});

const QUARTERS = [
  { label: "Q1 (Jan – Mar)", value: 1 },
  { label: "Q2 (Apr – Jun)", value: 2 },
  { label: "Q3 (Jul – Sep)", value: 3 },
  { label: "Q4 (Oct – Dec)", value: 4 },
];

const MONTHS = [
  { label: "January", value: 1 }, { label: "February", value: 2 }, { label: "March", value: 3 },
  { label: "April", value: 4 }, { label: "May", value: 5 }, { label: "June", value: 6 },
  { label: "July", value: 7 }, { label: "August", value: 8 }, { label: "September", value: 9 },
  { label: "October", value: 10 }, { label: "November", value: 11 }, { label: "December", value: 12 },
];

const ACTIVITY_TYPES = [
  { key: "hospitalVisits", label: "Hospital Visits" },
  { key: "supervision", label: "Supervision" },
  { key: "mentoring", label: "Mentoring" },
  { key: "dataCollection", label: "Data Collection" },
];

const emptyActivities = () => ({ hospitalVisits: "", supervision: "", mentoring: "", dataCollection: "" });

class HemovigilancePlan extends React.Component {
  state = {
    isAdmin: false,
    roleCenter: null,

    filterYear: currentYear,
    filterMonth: null,
    filterQuarter: null,
    filterCenter: null,
    centers: [],
    plans: [],
    isLoading: false,

    showModal: false,
    showDeleteModal: false,
    showPerformModal: false,
    editRecord: null,
    deleteId: null,
    performRecord: null,

    formCenter: null,
    formDate: moment().format("YYYY-MM-DD"),
    formPlanned: emptyActivities(),
    formError: {},
    isSaving: false,
    isDeleting: false,

    perfPerformed: emptyActivities(),
    perfRemarks: "",
    isSavingPerf: false,
  };

  async componentDidMount() {
    await this.loadRole();
    this.getCenters();
    this.fetchPlans();
  }

  loadRole = async () => {
    const { centerRole } = await defineRoleCenterSite();
    const user = await getStorage();
    const isSuperAdmin = user?.role === "superAdmin";
    const isCentral = hasAllCenters(user?.center);
    const isAdmin = isSuperAdmin || isCentral || !centerRole;
    this.setState({ isAdmin, roleCenter: centerRole });
  };

  getCenters = async () => {
    const data = await centerQuery(this.props.defaultLanguage, { type: "dropdown" });
    this.setState({ centers: data || [] });
  };

  fetchPlans = async () => {
    const { filterYear, filterMonth, filterQuarter, filterCenter, isAdmin, roleCenter } = this.state;
    this.setState({ isLoading: true });
    try {
      const params = { year: filterYear, limit: 500, page: 1, sortField: "date", sortOrder: 1 };
      if (filterMonth?.value) params.month = filterMonth.value;
      if (filterQuarter?.value) params.quarter = filterQuarter.value;

      if (isAdmin) {
        if (filterCenter?.value) params.center = filterCenter.value;
      } else {
        if (roleCenter) params.center = roleCenter?._id || roleCenter;
      }

      const res = await fetchHemovigilancePlan(this.props.defaultLanguage, params);
      this.setState({ plans: res?.data || [], isLoading: false });
    } catch {
      this.setState({ isLoading: false });
    }
  };

  openAddModal = () => {
    this.setState({
      showModal: true,
      editRecord: null,
      formCenter: null,
      formDate: moment().format("YYYY-MM-DD"),
      formPlanned: emptyActivities(),
      formError: {},
    });
  };

  openEditModal = (record) => {
    const formCenter = record.center
      ? { label: record.center.name || record.center._id, value: record.center._id || record.center }
      : null;
    this.setState({
      showModal: true,
      editRecord: record,
      formCenter,
      formDate: moment(record.date).format("YYYY-MM-DD"),
      formPlanned: {
        hospitalVisits: String(record.planned?.hospitalVisits ?? ""),
        supervision: String(record.planned?.supervision ?? ""),
        mentoring: String(record.planned?.mentoring ?? ""),
        dataCollection: String(record.planned?.dataCollection ?? ""),
      },
      formError: {},
    });
  };

  openPerformModal = (record) => {
    this.setState({
      showPerformModal: true,
      performRecord: record,
      perfPerformed: {
        hospitalVisits: record.performed?.hospitalVisits != null ? String(record.performed.hospitalVisits) : "",
        supervision: record.performed?.supervision != null ? String(record.performed.supervision) : "",
        mentoring: record.performed?.mentoring != null ? String(record.performed.mentoring) : "",
        dataCollection: record.performed?.dataCollection != null ? String(record.performed.dataCollection) : "",
      },
      perfRemarks: record.remarks || "",
    });
  };

  closeModal = () => this.setState({ showModal: false, editRecord: null });
  closePerformModal = () => this.setState({ showPerformModal: false, performRecord: null });

  validateForm = () => {
    const { formCenter, formDate } = this.state;
    const errors = {};
    if (!formCenter) errors.formCenter = "Required";
    if (!formDate) errors.formDate = "Required";
    this.setState({ formError: errors });
    return Object.keys(errors).length === 0;
  };

  saveRecord = async () => {
    if (!this.validateForm()) return;
    const { formCenter, formDate, formPlanned, editRecord } = this.state;

    this.setState({ isSaving: true });
    try {
      const planned = {};
      ACTIVITY_TYPES.forEach(({ key }) => {
        planned[key] = Number(formPlanned[key]) || 0;
      });

      const body = { center: formCenter.value, date: formDate, planned };

      if (editRecord) {
        body.id = editRecord._id;
        await updateHemovigilancePlan(this.props.defaultLanguage, body);
        toastMessage("success", "Plan updated");
      } else {
        await createHemovigilancePlan(this.props.defaultLanguage, body);
        toastMessage("success", "Plan created");
      }

      this.closeModal();
      this.fetchPlans();
    } catch (err) {
      toastMessage("error", err?.response?.data?.error || "Save failed");
    }
    this.setState({ isSaving: false });
  };

  savePerformance = async () => {
    const { performRecord, perfPerformed, perfRemarks } = this.state;
    this.setState({ isSavingPerf: true });
    try {
      const performed = {};
      ACTIVITY_TYPES.forEach(({ key }) => {
        performed[key] = Number(perfPerformed[key]) || 0;
      });

      await recordHemovigilancePerformance(this.props.defaultLanguage, {
        id: performRecord._id,
        performed,
        remarks: perfRemarks,
      });
      toastMessage("success", "Performance recorded");
      this.closePerformModal();
      this.fetchPlans();
    } catch (err) {
      toastMessage("error", err?.response?.data?.error || "Save failed");
    }
    this.setState({ isSavingPerf: false });
  };

  confirmDelete = (id) => this.setState({ showDeleteModal: true, deleteId: id });
  closeDeleteModal = () => this.setState({ showDeleteModal: false, deleteId: null });

  deleteRecord = async () => {
    this.setState({ isDeleting: true });
    try {
      await deleteHemovigilancePlan(this.props.defaultLanguage, this.state.deleteId);
      toastMessage("success", "Plan deleted");
      this.closeDeleteModal();
      this.fetchPlans();
    } catch {
      toastMessage("error", "Delete failed");
    }
    this.setState({ isDeleting: false });
  };

  renderSummaryTable() {
    const { plans, filterYear } = this.state;
    if (!plans.length) return null;

    const byCenter = {};
    plans.forEach((p) => {
      const centerId = p.center?._id || p.center;
      const centerName = p.center?.name || centerId;
      if (!byCenter[centerId]) {
        byCenter[centerId] = { centerName, planned: emptyActivities(), performed: emptyActivities(), days: 0 };
        ACTIVITY_TYPES.forEach(({ key }) => { byCenter[centerId].planned[key] = 0; byCenter[centerId].performed[key] = 0; });
      }
      ACTIVITY_TYPES.forEach(({ key }) => {
        byCenter[centerId].planned[key] += p.planned?.[key] || 0;
        byCenter[centerId].performed[key] += p.performed?.[key] || 0;
      });
      byCenter[centerId].days += 1;
    });

    return (
      <div id="printable-hemovigi-plan">
        <style>{`@media print { body > *:not(#printable-hemovigi-plan) { display: none; } #printable-hemovigi-plan { display: block !important; } .no-print { display: none !important; } }`}</style>
        <div style={{ textAlign: "center", fontWeight: "bold", fontSize: 15, marginBottom: 4 }}>HEMOVIGILANCE & CORNEAVIGILANCE ACTIVITY PLAN — {filterYear}</div>
        <div style={{ textAlign: "center", fontSize: 12, color: "#555", marginBottom: 12 }}>Rwanda Biomedical Centre / Blood Transfusion Division</div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#7b1fa2", color: "#fff" }}>
                <th style={thStyle}>Center (RCBT)</th>
                {ACTIVITY_TYPES.map(({ label }) => (
                  <th key={label} style={thStyle} colSpan={2}>{label}</th>
                ))}
                <th style={thStyle}>Days Tracked</th>
              </tr>
              <tr style={{ background: "#9c27b0", color: "#fff" }}>
                <th style={thStyle}></th>
                {ACTIVITY_TYPES.map(({ key }) => (
                  <React.Fragment key={key}>
                    <th style={{ ...thStyle, fontSize: 10 }}>Planned</th>
                    <th style={{ ...thStyle, fontSize: 10 }}>Performed</th>
                  </React.Fragment>
                ))}
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(byCenter).map(([centerId, d]) => (
                <tr key={centerId}>
                  <td style={tdStyle}>{d.centerName}</td>
                  {ACTIVITY_TYPES.map(({ key }) => (
                    <React.Fragment key={key}>
                      <td style={{ ...tdStyle, textAlign: "center" }}>{d.planned[key]}</td>
                      <td style={{ ...tdStyle, textAlign: "center", color: d.performed[key] >= d.planned[key] && d.planned[key] > 0 ? "#1a7c3e" : d.performed[key] > 0 ? "#c0392b" : "#888" }}>
                        {d.performed[key] || "—"}
                      </td>
                    </React.Fragment>
                  ))}
                  <td style={{ ...tdStyle, textAlign: "center" }}>{d.days}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: "#f0f0f0", fontWeight: "bold" }}>
                <td style={tdStyle}>TOTAL</td>
                {ACTIVITY_TYPES.map(({ key }) => {
                  const totPl = plans.reduce((s, p) => s + (p.planned?.[key] || 0), 0);
                  const totPf = plans.reduce((s, p) => s + (p.performed?.[key] || 0), 0);
                  return (
                    <React.Fragment key={key}>
                      <td style={{ ...tdStyle, textAlign: "center" }}>{totPl}</td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>{totPf}</td>
                    </React.Fragment>
                  );
                })}
                <td style={{ ...tdStyle, textAlign: "center" }}>{plans.length}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  }

  renderPlanList() {
    const { plans, isLoading } = this.state;
    if (isLoading) return <div style={{ padding: 24, textAlign: "center" }}>Loading...</div>;
    if (!plans.length) return <div style={{ padding: 24, textAlign: "center", color: "#888" }}>No records found. Click "+ Add" to create one.</div>;

    return (
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#f0f0f0" }}>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>W</th>
              <th style={thStyle}>Center</th>
              {ACTIVITY_TYPES.map(({ label }) => (
                <th key={label} style={thStyle} colSpan={2}>{label.replace(" ", "\n")}</th>
              ))}
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Actions</th>
            </tr>
            <tr style={{ background: "#f8f8f8" }}>
              <th style={thStyle}></th>
              <th style={thStyle}></th>
              <th style={thStyle}></th>
              {ACTIVITY_TYPES.map(({ key }) => (
                <React.Fragment key={key}>
                  <th style={{ ...thStyle, fontSize: 10, color: "#666" }}>Pl</th>
                  <th style={{ ...thStyle, fontSize: 10, color: "#666" }}>Pf</th>
                </React.Fragment>
              ))}
              <th style={thStyle}></th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p._id}>
                <td style={tdStyle}>{moment(p.date).format("DD/MM/YYYY")}</td>
                <td style={{ ...tdStyle, textAlign: "center" }}>W{p.week}</td>
                <td style={tdStyle}>{p.center?.name || "—"}</td>
                {ACTIVITY_TYPES.map(({ key }) => (
                  <React.Fragment key={key}>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{p.planned?.[key] ?? "—"}</td>
                    <td style={{ ...tdStyle, textAlign: "center", color: p.performed?.[key] != null ? (p.performed[key] >= (p.planned?.[key] || 0) ? "#1a7c3e" : "#c0392b") : "#aaa" }}>
                      {p.performed?.[key] ?? "—"}
                    </td>
                  </React.Fragment>
                ))}
                <td style={{ ...tdStyle, textAlign: "center" }}>
                  <span style={statusBadge(p.status)}>{p.status}</span>
                </td>
                <td style={{ ...tdStyle, textAlign: "center" }}>
                  <button onClick={() => this.openPerformModal(p)} style={actionBtnStyle("#1a7c3e")}>✓ Record</button>
                  {" "}
                  <button onClick={() => this.openEditModal(p)} style={actionBtnStyle("#2980b9")}>Edit</button>
                  {" "}
                  <button onClick={() => this.confirmDelete(p._id)} style={actionBtnStyle("#c0392b")}>Del</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    const {
      filterYear, filterMonth, filterQuarter, filterCenter, centers,
      showModal, showDeleteModal, showPerformModal, editRecord,
      formCenter, formDate, formPlanned, formError, isSaving, isDeleting,
      perfPerformed, perfRemarks, isSavingPerf, performRecord, plans,
    } = this.state;

    return (
      <div>
        <div className="no-print row" style={{ marginBottom: 16, alignItems: "flex-end" }}>
          <div className="col-md-2">
            <Select label="Year" options={YEARS} value={YEARS.find((y) => y.value === filterYear) || null}
              onChange={(v) => this.setState({ filterYear: v?.value || currentYear })} />
          </div>
          <div className="col-md-2">
            <Select label="Quarter" options={QUARTERS} value={filterQuarter}
              onChange={(v) => this.setState({ filterQuarter: v })} isClearable />
          </div>
          <div className="col-md-2">
            <Select label="Month" options={MONTHS} value={filterMonth}
              onChange={(v) => this.setState({ filterMonth: v })} isClearable />
          </div>
          <div className="col-md-2">
            <Select label="Center" options={centers} value={filterCenter}
              onChange={(v) => this.setState({ filterCenter: v })} isClearable />
          </div>
          <div className="col-md-4" style={{ display: "flex", gap: 6, alignItems: "flex-end" }}>
            <Button text="Search" onPress={this.fetchPlans} />
            <Button text="+ Add" onPress={this.openAddModal} className="btn-default" />
            {plans.length > 0 && <Button text="Print" onPress={() => window.print()} className="btn-default" />}
          </div>
        </div>

        {this.renderSummaryTable()}

        <div style={{ marginTop: 24 }} className="no-print">
          <div style={{ fontWeight: "bold", fontSize: 14, marginBottom: 8, borderBottom: "1px solid #ddd", paddingBottom: 4 }}>Daily Records</div>
          {this.renderPlanList()}
        </div>

        {/* Add / Edit Modal */}
        <Modal show={showModal} handleClose={this.closeModal} title={editRecord ? "Edit Hemovigilance Plan" : "Add Hemovigilance Plan"} size="md">
          <div className="modal-body">
            <div className="row">
              <div className="col-md-12">
                <Select label="Center (RCBT)" options={centers} value={formCenter}
                  onChange={(v) => this.setState({ formCenter: v })} error={formError.formCenter} />
              </div>
              <div className="col-md-12">
                <Input label="Date" type="date" value={formDate}
                  onChange={(e) => this.setState({ formDate: e.target.value })} error={formError.formDate} />
              </div>
              <div className="col-md-12">
                <div style={{ fontWeight: "bold", fontSize: 13, margin: "10px 0 6px", color: "#555" }}>Planned Activities</div>
              </div>
              {ACTIVITY_TYPES.map(({ key, label }) => (
                <div className="col-md-6" key={key}>
                  <Input label={label} type="number" value={formPlanned[key]}
                    onChange={(e) => this.setState({ formPlanned: { ...formPlanned, [key]: e.target.value } })} />
                </div>
              ))}
            </div>
          </div>
          <div className="modal-footer">
            <Button text="Cancel" onPress={this.closeModal} className="btn-default" />
            <Button text={editRecord ? "Update" : "Save"} onPress={this.saveRecord} isSubmitting={isSaving} />
          </div>
        </Modal>

        {/* Performance Modal */}
        <Modal show={showPerformModal} handleClose={this.closePerformModal}
          title={`Record Performance — ${performRecord ? moment(performRecord.date).format("DD/MM/YYYY") : ""}`} size="md">
          <div className="modal-body">
            {performRecord && (
              <div style={{ background: "#f8f9fa", borderRadius: 6, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
                <strong>Center:</strong> {performRecord.center?.name || "—"} &nbsp; <strong>Date:</strong> {moment(performRecord.date).format("DD/MM/YYYY")}
              </div>
            )}
            <div style={{ fontWeight: "bold", fontSize: 13, margin: "0 0 8px", color: "#555" }}>Performed Activities</div>
            <div className="row">
              {ACTIVITY_TYPES.map(({ key, label }) => (
                <div className="col-md-6" key={key}>
                  <Input label={label}
                    placeholder={`Planned: ${performRecord?.planned?.[key] ?? 0}`}
                    type="number" value={perfPerformed[key]}
                    onChange={(e) => this.setState({ perfPerformed: { ...perfPerformed, [key]: e.target.value } })} />
                </div>
              ))}
              <div className="col-md-12">
                <Input label="Remarks" value={perfRemarks}
                  onChange={(e) => this.setState({ perfRemarks: e.target.value })} />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <Button text="Cancel" onPress={this.closePerformModal} className="btn-default" />
            <Button text="Save Performance" onPress={this.savePerformance} isSubmitting={isSavingPerf} />
          </div>
        </Modal>

        <Modal show={showDeleteModal} handleClose={this.closeDeleteModal} title="Delete Plan">
          <DeleteModal handleCloseModal={this.closeDeleteModal} onDelete={this.deleteRecord} isDeleting={isDeleting} />
        </Modal>
      </div>
    );
  }
}

const thStyle = { border: "1px solid #ccc", padding: "6px 8px", textAlign: "left", fontWeight: "bold", fontSize: 12, whiteSpace: "nowrap" };
const tdStyle = { border: "1px solid #ddd", padding: "5px 8px", whiteSpace: "nowrap" };
const actionBtnStyle = (color) => ({ background: color, color: "#fff", border: "none", borderRadius: 3, padding: "3px 8px", cursor: "pointer", fontSize: 11 });
const statusBadge = (status) => ({
  display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: "bold",
  background: status === "performed" ? "#d4edda" : status === "cancelled" ? "#f8d7da" : "#fff3cd",
  color: status === "performed" ? "#155724" : status === "cancelled" ? "#721c24" : "#856404",
});

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return { defaultLanguage };
};

export default connect(mapStateToProps)(HemovigilancePlan);
